import cloneDeep from "lodash-es/cloneDeep";
import isArray from "lodash-es/isArray";
import lodash_isEmpty from "lodash-es/isEmpty";
import isFunction from "lodash-es/isFunction";
import isNumber from "lodash-es/isNumber";
import isObject from "lodash-es/isObject";
import isString from "lodash-es/isString";
import mergeWith from "lodash-es/mergeWith";

/* eslint-disable replo/no-export-from */
// TODO (Noah, 2026-02-20): These re-exports are intentional - replo-utils/lib/misc is a
// convenience module that re-exports commonly used lodash utilities for consumers. We
// should consider removing these in the future
export { isFunction, isNumber, isString };
/* eslint-enable replo/no-export-from */

/**
 * Checks if a value is neither `null` nor `undefined`.
 */
export function isNotNullish<T>(
  value: T,
): value is Exclude<T, null | undefined> {
  return value != null;
}

/**
 * Checks if a value is either `null` or `undefined`.
 */
export function isNullish(value: unknown): value is null | undefined {
  return value == null;
}

export function isPrimitive(
  value: unknown,
): value is string | number | boolean {
  const type = typeof value;
  return ["string", "number", "boolean"].includes(type);
}

type StructureRecord<A extends { type: string }, R> = {
  [T in A as T["type"]]: R | ((value: T) => R);
};

// Note: This is a more generic version of StructureRecord that works with any discriminant key
type StructureRecordGeneric<
  A extends Record<K, string>,
  R,
  K extends keyof A,
> = {
  [T in A as T[K]]: R | ((value: T) => R);
};

/**
 * @returns A function which can be used to create an exhaustive switch over all
 * types of a discriminated union, based on a given key.
 *
 * When no key is provided, it defaults to using "type" as the discriminant.
 *
 * Examples:
 *
 * With default "type" discriminant:
 * ```
 * type OneOrTwo = { type: "one", numberValue: number } |
 * { type: "two", stringValue: string }
 *
 * let oneOrTwo = ...
 * let result = exhaustiveSwitch(oneOrTwo)({
 * one: (op) => `${op.numberValue + 1}`, // Typescript knows numberValue is a number
 * two: (op) => op.stringValue + "hello", // Typescript knows stringValue is a string
 * }) // type of result is inferred to be string
 * ```
 *
 * With custom discriminant key:
 * ```
 * type OneOrTwo = { variant: "one", numberValue: number } |
 * { variant: "two", stringValue: string }
 *
 * let oneOrTwo = ...
 * let result = exhaustiveSwitch(oneOrTwo, "variant")({
 * one: (op) => `${op.numberValue + 1}`, // Typescript knows numberValue is a number
 * two: (op) => op.stringValue + "hello", // Typescript knows stringValue is a string
 * }) // type of result is inferred to be string
 * ```
 *
 * Typescript will error if you forget a case, or if you assume the wrong type
 * for a case value. The switch also returns the value you return from the
 * function mapping, so it works well for "extension" methods where you want to
 * map over all discriminated union cases and do a different thing for each.
 *
 * Works well for many of our types, like ComponentActionType, Action, etc
 */
// Overload for the default case with "type" discriminant
export function exhaustiveSwitch<A extends { type: string }>(
  value: A,
): <R>(cases: StructureRecord<A, R>) => R;
// Overload for custom discriminant key
export function exhaustiveSwitch<
  A extends Record<K, string>,
  K extends keyof A,
>(value: A, key: K): <R>(cases: StructureRecordGeneric<A, R, K>) => R;
// Implementation
export function exhaustiveSwitch(value: any, discriminantKey: any = "type") {
  return (cases: any): any => {
    const discriminant = value[discriminantKey];
    const f = cases[discriminant];
    if (!isFunction(f)) {
      return f;
    }
    return f(value);
  };
}

type EmptyObject<T> = { [K in keyof T]?: never };
type EmptyObjectOf<T> = EmptyObject<T> extends T ? EmptyObject<T> : never;

/**
 * The same as `isEmpty` from lodash, but with a few additional overloads for
 * stronger type-checking on empty arrays.
 */
export function isEmpty(value: string): value is "";
export function isEmpty<T extends any[]>(
  value: T | null | undefined,
): value is (T & []) | null | undefined;
export function isEmpty<T extends ArrayLike<any>>(
  value: T | null | undefined,
): value is (T & { length: 0 }) | null | undefined;
export function isEmpty(
  value: Map<any, any> | Set<any> | ArrayLike<any> | null | undefined,
): boolean;
export function isEmpty(value: object): boolean;
export function isEmpty<T extends object>(
  value: T | null | undefined,
): value is EmptyObjectOf<T> | null | undefined;
export function isEmpty(value?: any): boolean;

export function isEmpty(value: any) {
  return lodash_isEmpty(value);
}

// biome-ignore lint/suspicious/noEmptyBlockStatements: allow empty object noop
export function noop() {}

export function preventDefault(event: { preventDefault(): void }) {
  event.preventDefault();
}

/**
 * Copyright (c) 2019 Alexander Reardon
 * https://github.com/alexreardon/tiny-warning/blob/master/src/index.js
 */
export function warning(
  condition: unknown,
  message: string | (() => string),
  options?: { severity: "log" | "warn" | "error" },
) {
  if (condition) {
    return;
  }

  const prefix = "[REPLO]";
  const provided = isFunction(message) ? message() : message;
  const text = provided ? `${prefix}: ${provided}` : prefix;
  const severity = options?.severity || "warn";
  // biome-ignore lint/suspicious/noConsole: Utility warning function for debugging
  console[severity](text);
  // Throwing an error and catching it immediately to improve debugging. A
  // consumer can use 'pause on caught exceptions'
  // https://github.com/facebook/react/issues/4216
  try {
    // eslint-disable-next-line replo/no-raw-error-throw
    throw new Error(text);
  } catch {
    // allow empty catch
  }
}

/**
 *
 * Copyright (c) 2019 Alexander Reardon
 * https://github.com/alexreardon/tiny-warning/blob/master/src/index.js
 */
export function invariant(
  condition: unknown,
  message: string | (() => string),
  opts?: { stripErrorMessage: unknown },
): asserts condition {
  const prefix = "[REPLO] Invariant failed";
  if (condition) {
    return;
  }

  if (opts?.stripErrorMessage) {
    // eslint-disable-next-line replo/no-raw-error-throw
    throw new Error(prefix);
  }

  const provided = isFunction(message) ? message() : message;
  const text = provided ? `${prefix}: ${provided}` : prefix;
  // eslint-disable-next-line replo/no-raw-error-throw
  throw new Error(text);
}

export const mergeReplacingObjects = (first: Object, second: Object) => {
  mergeWith(first, second, (_a, b) => {
    return isObject(b) && isEmpty(b) ? b : undefined;
  });
};

export const mergeReplacingArrays = (first: Object, second: Object) => {
  mergeWith(first, second, (_a, b) => (isArray(b) ? b : undefined));
};

export const deepCloneAndMergeReplacingArrays = <T>(
  source: T,
  ...toMerge: Object[]
): T => {
  const copy = cloneDeep(source);
  for (const object of toMerge) {
    mergeReplacingArrays(copy as Object, object);
  }
  return copy;
};

/**
 * hasOwnProperty with a type assertion. If this returns true, typescript knows that `obj`
 * has a property called `prop` from that point forward
 * https://fettblog.eu/typescript-hasownproperty/
 */
export function hasOwnProperty<
  X extends {} | null | undefined,
  Y extends PropertyKey,
>(obj: X, prop: Y): obj is X & Record<Y, unknown> {
  return obj?.hasOwnProperty(prop) || false;
}

/**
 * Given a string | number param, converts it to a string, unless it's nullish
 * in which case it just returns it. Useful for times when you have string | number
 * | nullish types, like when selecting the current value of a CSS property in a right
 * bar modifier
 */
export function coerceNumberToString(value: string | number): string;
export function coerceNumberToString(
  value: string | number | null | undefined,
): string | null | undefined;
export function coerceNumberToString(
  value: string | number | undefined,
): string | undefined;
export function coerceNumberToString(
  value: string | number | null,
): string | null;
export function coerceNumberToString(value: any): any {
  if (value !== null && value !== undefined) {
    return String(value);
  }
  return value;
}

/**
 * Formats a file size in bytes to a human readable string with appropriate
 * units. Rounds to 0 decimal places unless otherwise specified and uses
 * standard binary units (KB, MB, etc).
 */
export function formatFileSize(byteCount: number, decimalPlaces = 0): string {
  if (byteCount === 0) {
    return "0 Bytes";
  }

  const bytesPerUnit = 1024;
  const binaryUnits = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  // Calculate which unit to use based on the size
  const unitIndex = Math.floor(Math.log(byteCount) / Math.log(bytesPerUnit));

  // Convert to the appropriate unit and round to decimal places
  const sizeInUnit = byteCount / Math.pow(bytesPerUnit, unitIndex);
  const formattedSize = Number.parseFloat(sizeInUnit.toFixed(decimalPlaces));

  return `${formattedSize} ${binaryUnits[unitIndex]}`;
}

export const sleep = async (ms: number) => {
  return await new Promise((resolve) => setTimeout(resolve, ms));
};
